import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Grid,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { Visibility as VisibilityIcon, Add as AddIcon } from "@mui/icons-material";
import moment from "moment";

const statusColors = {
  1: "default",
  2: "primary",
  3: "warning",
  4: "success",
  5: "error",
};

export default function InvoicesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [page] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [patientFilter, setPatientFilter] = useState("");

  const queryParams = new URLSearchParams();
  queryParams.set("page", page);
  queryParams.set("page_size", "50");
  if (statusFilter) queryParams.set("status", statusFilter);
  if (patientFilter) queryParams.set("patient", patientFilter);

  const { data, isLoading, isError, error } = useFetchObjects(
    ["invoices", page, statusFilter, patientFilter],
    `invoices/?${queryParams.toString()}`,
    token
  );

  const invoices = data?.results || data || [];

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || "Failed to load invoices"}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {t("finance.invoices", { defaultValue: "Invoices" })}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/finance/invoices/add")}
                >
                  {t("finance.addInvoice", { defaultValue: "Add Invoice" })}
                </Button>
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  label={t("finance.patientId", { defaultValue: "Patient ID" })}
                  value={patientFilter}
                  onChange={(e) => setPatientFilter(e.target.value)}
                  placeholder="ID"
                  sx={{ minWidth: 120 }}
                />
              </Grid>
              <Grid item>
                <TextField
                  select
                  size="small"
                  label={t("finance.status")}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="">{t("finance.all", { defaultValue: "All" })}</MenuItem>
                  <MenuItem value="1">{t("finance.draft", { defaultValue: "Draft" })}</MenuItem>
                  <MenuItem value="2">{t("finance.issued", { defaultValue: "Issued" })}</MenuItem>
                  <MenuItem value="3">{t("finance.partial", { defaultValue: "Partially Paid" })}</MenuItem>
                  <MenuItem value="4">{t("finance.paid", { defaultValue: "Paid" })}</MenuItem>
                  <MenuItem value="5">{t("finance.cancelled", { defaultValue: "Cancelled" })}</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.200" }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>{t("finance.patient")}</strong></TableCell>
                  <TableCell><strong>{t("finance.date")}</strong></TableCell>
                  <TableCell><strong>{t("finance.total")}</strong></TableCell>
                  <TableCell><strong>{t("finance.paid")}</strong></TableCell>
                  <TableCell><strong>{t("finance.status")}</strong></TableCell>
                  <TableCell align="right"><strong>{t("finance.action")}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      {t("finance.noRecords", { defaultValue: "No invoices found." })}
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.patient_name_display || `#${row.patient}`}</TableCell>
                      <TableCell>{row.invoice_date ? moment(row.invoice_date).format("YYYY-MM-DD") : "—"}</TableCell>
                      <TableCell>{row.total_amount != null ? Number(row.total_amount).toFixed(2) : "—"}</TableCell>
                      <TableCell>{row.amount_paid != null ? Number(row.amount_paid).toFixed(2) : "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status_display}
                          color={statusColors[row.status] || "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/finance/invoices/${row.id}`)}
                        >
                          {t("finance.view", { defaultValue: "View" })}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
